import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';

import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsProject } from '../../../../common/models/content-studio.model';
import { ProjectCard } from '../../../../modules/vendor/content-studio/components/ProjectCard';
import { clearBrief, peekBrief } from '../../../../common/services/content-brief.service';
import { NewProjectDialog } from '../../../../modules/vendor/content-studio/components/NewProjectDialog';
import { CreditBadge } from '../../../../modules/vendor/content-studio/components/CreditBadge';
import { PageContainer } from '../../../../common/components/PageContainer';

const ContentStudioProjectsPage: React.FC = () => {
    const { t } = useTranslation();
    const api = useContentStudioApi();
    const navigate = useNavigate();
    /**
     * A brief handed over by an insight.
     *
     * The dashboard's "make images for this product" action lands here rather
     * than creating anything: content belongs to a project and the vendor
     * should choose which one. A finding is a reason to work, not permission to
     * make things on their behalf. Shown, not consumed — the composer in the
     * workspace is what finally takes it.
     */
    const [briefDismissed, setBriefDismissed] = useState(false);
    const handoverBrief = briefDismissed ? null : peekBrief();

    const [projects, setProjects] = useState<CsProject[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [balance, setBalance] = useState<number | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);

    const loadProjects = useCallback(
        async (searchValue?: string) => {
            setLoading(true);
            try {
                const res = await api.listProjects({ search: searchValue, limit: 48 });
                setProjects(res.items || []);
            } catch (err) {
                console.error('Failed to load projects', err);
            } finally {
                setLoading(false);
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const loadBalance = useCallback(async () => {
        try {
            const res = await api.getCreditBalance();
            setBalance(res.balance);
        } catch {
            setBalance(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        loadProjects('');
        loadBalance();
    }, [loadProjects, loadBalance]);

    useEffect(() => {
        const handle = setTimeout(() => loadProjects(search), 300);
        return () => clearTimeout(handle);
    }, [search, loadProjects]);

    const handleCreate = async (name: string, description: string) => {
        const created = await api.createProject({ name, description });
        // Open the freshly-created project straight away — a new project is
        // made to be worked in, so drop the vendor onto its canvas rather than
        // back on the list. Any brief that sent us here goes with it.
        openProject(created.uuid);
    };

    /**
     * Open a project, forwarding an insight's brief if one brought us here.
     *
     * Every route into the workspace goes through this, so the handover works
     * whether the vendor makes a new project for the job or opens one they
     * already had.
     */
    const openProject = (uuid: string) => {
        navigate(`/marketing/content-studio/${uuid}`);
    };

    return (
        <PageContainer
            breadcrumbs={[
                { label: t('SIDEBAR.MARKETING.TITLE', 'Marketing') },
                { label: t('SIDEBAR.MARKETING.CONTENT_STUDIO', 'Content Studio') },
            ]}
        >
            {/*
              * What the vendor came here to do.
              *
              * Without this the handover is invisible: they click "make
              * images for this product" on the dashboard and arrive at an
              * ordinary project list, with the reason left behind on the
              * previous page.
              */}
            {handoverBrief && (
                <Card className="mb-6">
                    <CardContent className="flex items-start gap-3">
                        <div className="min-w-0 flex-1">
                            <p className="mb-1 text-xs font-medium tracking-wider text-muted-foreground uppercase">
                                {t('CONTENT_STUDIO.BRIEF.TITLE', 'From your dashboard')}
                            </p>
                            <p className="text-sm/relaxed text-foreground">{handoverBrief.text}</p>
                            <p className="mt-1.5 text-xs text-muted-foreground">
                                {t(
                                    'CONTENT_STUDIO.BRIEF.HINT',
                                    'Open a project or start a new one — this brief will be waiting in the composer.',
                                )}
                            </p>
                        </div>
                        <Button
                            variant="ghost"
                            onClick={() => { clearBrief(); setBriefDismissed(true); }}
                        >
                            {t('CONTENT_STUDIO.BRIEF.DISMISS', 'Dismiss')}
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Sub-header: search + actions */}
            <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
                <div className="relative w-full sm:w-80">
                    <Search className="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('CONTENT_STUDIO.SEARCH_PLACEHOLDER', 'Search projects…') || ''}
                        className="h-9 ps-8"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <CreditBadge balance={balance} loading={balance === null && !loading} />

                    <Button variant="outline" className="h-9" onClick={() => setDialogOpen(true)}>
                        <Plus className="size-4" />
                        {t('CONTENT_STUDIO.NEW_PROJECT', 'New project')}
                    </Button>
                </div>
            </div>

            {/* Grid */}
            {loading && projects.length === 0 ? (
                <div className="py-20 text-center text-sm text-muted-foreground">
                    {t('CONTENT_STUDIO.LOADING', 'Loading projects…')}
                </div>
            ) : projects.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-card p-12 text-center">
                    <p className="mb-2 font-heading text-sm font-medium tracking-tight text-foreground">
                        {t('CONTENT_STUDIO.EMPTY_TITLE', 'No projects yet')}
                    </p>
                    <p className="text-xs text-muted-foreground">
                        {t(
                            'CONTENT_STUDIO.EMPTY_HINT',
                            'Create your first project to start generating on-brand content for your catalog.',
                        )}
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
                    {projects.map((p) => (
                        <ProjectCard
                            key={p.uuid}
                            project={p}
                            onDelete={async (uuid) => {
                                try {
                                    await api.deleteProject(uuid);
                                    setProjects((prev) => prev.filter((pr) => pr.uuid !== uuid));
                                } catch { /* ignore */ }
                            }}
                            onDuplicate={async (uuid) => {
                                try {
                                    const source = projects.find((pr) => pr.uuid === uuid);
                                    if (!source) return;
                                    // Create new project with same name + " (copy)"
                                    const created = await api.createProject({
                                        name: `${source.name} (copy)`,
                                        description: source.description,
                                        linkedProductUuids: source.linkedProductUuids,
                                    });
                                    // Clone canvasGraph (product + recipe nodes only, no generations)
                                    if (source.canvasGraph && source.canvasGraph.length > 0) {
                                        const clonedGraph = source.canvasGraph.filter(
                                            (n: any) => n.kind === 'product' || n.kind === 'recipe',
                                        ).map((n: any) => {
                                            if (n.kind === 'recipe') {
                                                return { ...n, generationNodeIds: [], generating: false };
                                            }
                                            return n;
                                        });
                                        await api.updateProject(created.uuid, { canvasGraph: clonedGraph });
                                        created.canvasGraph = clonedGraph;
                                    }
                                    setProjects((prev) => [created, ...prev]);
                                } catch { /* ignore */ }
                            }}
                            onRename={async (uuid, name, description) => {
                                try {
                                    await api.updateProject(uuid, { name, description });
                                    setProjects((prev) =>
                                        prev.map((pr) =>
                                            pr.uuid === uuid ? { ...pr, name, description } : pr,
                                        ),
                                    );
                                } catch { /* ignore */ }
                            }}
                        />
                    ))}
                </div>
            )}

            <NewProjectDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onCreate={handleCreate}
            />
        </PageContainer>
    );
};

export default ContentStudioProjectsPage;
